import React, { useState, Fragment } from 'react';
import { EditIcon, TrashIcon, EyeIcon, XCircleIcon, AlertTriangleIcon, CheckCircleIcon, InfoIcon, BellIcon, SettingsIcon, RefreshCwIcon, ClipboardIcon } from 'lucide-react';
import { Message } from './MessagesManagement';
import { StatusBadge } from '../../StatusBadge';
interface MessagesListProps {
  messages: Message[];
  canEditMessage: (message: Message) => boolean;
  onEdit: (message: Message) => void;
  onDelete: (id: string) => void;
  onResetOverride: (id: string) => void;
  locale: string;
}
export function MessagesList({
  messages,
  canEditMessage,
  onEdit,
  onDelete,
  onResetOverride,
  locale
}: MessagesListProps) {
  const [expandedMessage, setExpandedMessage] = useState<string | null>(null);
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const getTypeIcon = (type: Message['type']) => {
    switch (type) {
      case 'Error':
        return <XCircleIcon className="h-5 w-5 text-red-500" data-id="element-16489" />;
      case 'Warning':
        return <AlertTriangleIcon className="h-5 w-5 text-amber-500" data-id="element-16490" />;
      case 'Success':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" data-id="element-16491" />;
      case 'Info':
        return <InfoIcon className="h-5 w-5 text-blue-500" data-id="element-16492" />;
      case 'Notification':
        return <BellIcon className="h-5 w-5 text-purple-500" data-id="element-16493" />;
      case 'System':
        return <SettingsIcon className="h-5 w-5 text-gray-500" data-id="element-16494" />;
      default:
        return null;
    }
  };
  const getTypeColor = (type: Message['type']) => {
    switch (type) {
      case 'Error':
        return 'text-red-700 bg-red-50';
      case 'Warning':
        return 'text-amber-700 bg-amber-50';
      case 'Success':
        return 'text-green-700 bg-green-50';
      case 'Info':
        return 'text-blue-700 bg-blue-50';
      case 'Notification':
        return 'text-purple-700 bg-purple-50';
      case 'System':
        return 'text-gray-700 bg-gray-50';
      default:
        return 'text-gray-700 bg-gray-50';
    }
  };
  const handleCopyKey = (key: string) => {
    navigator.clipboard.writeText(key).then(() => {
      // Could show a toast notification here
      console.log(`Copied ${key} to clipboard`);
    }).catch(err => {
      console.error('Failed to copy: ', err);
    });
  };
  const toggleExpand = (id: string) => {
    if (expandedMessage === id) {
      setExpandedMessage(null);
    } else {
      setExpandedMessage(id);
    }
  };
  if (messages.length === 0) {
    return <div className="text-center py-12" data-id="element-16495">
        <InfoIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-16496" />
        <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-16497">
          No messages found
        </h3>
        <p className="mt-1 text-sm text-gray-500" data-id="element-16498">
          No messages match your current filters. Try adjusting your search
          criteria.
        </p>
      </div>;
  }
  return <div className="overflow-x-auto" data-id="element-16499">
      <table className="min-w-full divide-y divide-gray-200" data-id="element-16500">
        <thead data-id="element-16501">
          <tr data-id="element-16502">
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-16503">
              Type
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-16504">
              Key & Message
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-16505">
              Module
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-16506">
              Status
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-16507">
              Usage
            </th>
            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-16508">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-16509">
          {messages.map(message => <Fragment key={message.id} data-id="element-16510">
              <tr className={expandedMessage === message.id ? 'bg-gray-50' : 'hover:bg-gray-50'} data-id="element-16511">
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-16512">
                  <div className="flex items-center" data-id="element-16513">
                    <span className="flex-shrink-0" data-id="element-16514">
                      {getTypeIcon(message.type)}
                    </span>
                    <span className="ml-2 text-sm font-medium text-gray-900" data-id="element-16515">
                      {message.type}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4" data-id="element-16516">
                  <div data-id="element-16517">
                    <div className="flex items-center text-sm font-medium text-gray-900" data-id="element-16518">
                      <button onClick={() => handleCopyKey(message.key)} className="text-gray-500 hover:text-gray-700 mr-1" title="Copy key" data-id="element-16519">
                        <ClipboardIcon className="h-4 w-4" data-id="element-16520" />
                      </button>
                      <span className="font-mono" data-id="element-16521">{message.key}</span>
                    </div>
                    <div className="mt-1 text-sm text-gray-500 line-clamp-2" data-id="element-16522">
                      {message.overriddenMessage ? <span className="text-indigo-600 font-medium" data-id="element-16523">
                          {message.overriddenMessage}
                        </span> : message.defaultMessage}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-16524">
                  <div className="text-sm text-gray-900" data-id="element-16525">{message.module}</div>
                  {message.context && <div className="text-sm text-gray-500" data-id="element-16526">
                      {message.context}
                    </div>}
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-16527">
                  <div className="flex flex-col space-y-1" data-id="element-16528">
                    <StatusBadge status={message.isActive ? 'success' : 'error'} text={message.isActive ? 'Active' : 'Inactive'} data-id="element-16529" />
                    {message.isSystemLocked && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-16530">
                        System Locked
                      </span>}
                    {message.overriddenMessage && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-16531">
                        Overridden
                      </span>}
                    {message.editableByRoles.length > 0 && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800" data-id="element-16532">
                        RBAC Limited
                      </span>}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-16533">
                  <div data-id="element-16534">Count: {message.usageCount}</div>
                  <div data-id="element-16535">Last: {formatDate(message.lastUsedAt)}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-16536">
                  <div className="flex justify-end space-x-2" data-id="element-16537">
                    <button onClick={() => toggleExpand(message.id)} className="text-indigo-600 hover:text-indigo-900" title="View details" data-id="element-16538">
                      <EyeIcon className="h-5 w-5" data-id="element-16539" />
                    </button>
                    {canEditMessage(message) && <>
                        <button onClick={() => onEdit(message)} className="text-indigo-600 hover:text-indigo-900" title="Edit message" data-id="element-16540">
                          <EditIcon className="h-5 w-5" data-id="element-16541" />
                        </button>
                        {message.overriddenMessage && <button onClick={() => onResetOverride(message.id)} className="text-amber-600 hover:text-amber-900" title="Reset override" data-id="element-16542">
                            <RefreshCwIcon className="h-5 w-5" data-id="element-16543" />
                          </button>}
                        {!message.isSystemLocked && <button onClick={() => onDelete(message.id)} className="text-red-600 hover:text-red-900" title="Delete message" data-id="element-16544">
                            <TrashIcon className="h-5 w-5" data-id="element-16545" />
                          </button>}
                      </>}
                  </div>
                </td>
              </tr>
              {expandedMessage === message.id && <tr className="bg-gray-50" data-id="element-16546">
                  <td colSpan={6} className="px-6 py-4" data-id="element-16547">
                    <div className="space-y-4" data-id="element-16548">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-16549">
                        <div data-id="element-16550">
                          <h4 className="text-sm font-medium text-gray-700 mb-1" data-id="element-16551">
                            Default Message
                          </h4>
                          <div className={`p-3 rounded-md text-sm ${getTypeColor(message.type)}`} data-id="element-16552">
                            {message.defaultMessage}
                          </div>
                        </div>
                        {message.overriddenMessage && <div data-id="element-16553">
                            <h4 className="text-sm font-medium text-gray-700 mb-1" data-id="element-16554">
                              Overridden Message
                            </h4>
                            <div className={`p-3 rounded-md text-sm ${getTypeColor(message.type)}`} data-id="element-16555">
                              {message.overriddenMessage}
                            </div>
                          </div>}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-16556">
                        <div data-id="element-16557">
                          <h4 className="text-sm font-medium text-gray-700 mb-1" data-id="element-16558">
                            Created
                          </h4>
                          <div className="text-sm text-gray-500" data-id="element-16559">
                            <div data-id="element-16560">{formatDate(message.createdAt)}</div>
                            <div data-id="element-16561">By: {message.createdBy}</div>
                          </div>
                        </div>
                        {message.modifiedAt && <div data-id="element-16562">
                            <h4 className="text-sm font-medium text-gray-700 mb-1" data-id="element-16563">
                              Last Modified
                            </h4>
                            <div className="text-sm text-gray-500" data-id="element-16564">
                              <div data-id="element-16565">{formatDate(message.modifiedAt)}</div>
                              <div data-id="element-16566">By: {message.modifiedBy}</div>
                            </div>
                          </div>}
                        <div data-id="element-16567">
                          <h4 className="text-sm font-medium text-gray-700 mb-1" data-id="element-16568">
                            RBAC Permissions
                          </h4>
                          {message.editableByRoles.length > 0 ? <div className="flex flex-wrap gap-1" data-id="element-16569">
                              {message.editableByRoles.map(role => <span key={role} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800" data-id="element-16570">
                                  {role}
                                </span>)}
                            </div> : <div className="text-sm text-gray-500" data-id="element-16571">
                              {message.isSystemLocked ? 'System locked - no editing allowed' : 'No RBAC restrictions'}
                            </div>}
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>}
            </Fragment>)}
        </tbody>
      </table>
    </div>;
}